import React, { useRef, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Card,
  CardActionArea,
  CardContent,
  Stepper,
  Step,
  StepLabel,
  Link,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  PhoneAndroid,
  Key,
  QrCode2,
  ContentCopy,
  Check,
  CheckCircleOutline,
  SecurityOutlined,
  ErrorOutline,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import {
  generateTOTPSetup,
  verifyTOTPSetup,
  setupSMSMfa,
  verifySMSSetup,
  checkPhoneOnFile,
} from '@/store/thunks/authThunks';
import {
  selectMfaUserId,
  selectIsLoading,
} from '@/store/slices/authSlice';
import type { MFAMethod } from '@/services/mfaAPI';

const steps = ['Choose Method', 'Configure', 'Verify'];

export const MFASetupPage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const userId = useAppSelector(selectMfaUserId);
  const isLoading = useAppSelector(selectIsLoading);

  const [activeStep, setActiveStep] = React.useState(0);
  const [selectedMethod, setSelectedMethod] = React.useState<MFAMethod | null>(null);
  const [qrCodeUrl, setQrCodeUrl] = React.useState('');
  const [totpSecret, setTotpSecret] = React.useState('');
  const [manualEntryCode, setManualEntryCode] = React.useState('');
  const [phoneHint, setPhoneHint] = React.useState('');
  const [hasPhoneOnFile, setHasPhoneOnFile] = React.useState<boolean | null>(null);
  const [backupCodes, setBackupCodes] = React.useState<string[]>([]);
  const [code, setCode] = React.useState(['', '', '', '', '', '']);
  const [error, setError] = React.useState('');
  const [setupLoading, setSetupLoading] = React.useState(false);
  const [copied, setCopied] = React.useState(false);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    if (!userId) {
      navigate('/auth/login', { replace: true });
    }
  }, [userId, navigate]);

  const handleSelectTOTP = async () => {
    setSelectedMethod('totp');
    setError('');
    setSetupLoading(true);

    try {
      const response = await dispatch(generateTOTPSetup(userId!));
      setQrCodeUrl(response.qrCodeUrl || '');
      setTotpSecret(response.secret || '');
      setManualEntryCode(response.manualEntryCode || '');
      setActiveStep(1);
    } catch (err: any) {
      setError(err.message || 'Failed to generate QR code');
    } finally {
      setSetupLoading(false);
    }
  };

  const handleSelectSMS = async () => {
    setSelectedMethod('sms');
    setError('');
    setSetupLoading(true);

    try {
      const response = await dispatch(checkPhoneOnFile(userId!));
      if (response.hasPhone) {
        setHasPhoneOnFile(true);
        setPhoneHint(response.phoneHint || '');
      } else {
        setHasPhoneOnFile(false);
      }
      setActiveStep(1);
    } catch (err: any) {
      setHasPhoneOnFile(false);
      setActiveStep(1);
    } finally {
      setSetupLoading(false);
    }
  };

  const handleSendSmsCode = async () => {
    setSetupLoading(true);
    setError('');

    try {
      const response = await dispatch(setupSMSMfa(userId!));
      setPhoneHint(response.phoneHint || '');
      setActiveStep(2);
    } catch (err: any) {
      setError(err.message || 'Failed to send verification code');
    } finally {
      setSetupLoading(false);
    }
  };

  const handleCodeChange = (index: number, value: string) => {
    if (!/^\d*$/.test(value)) return;

    const newCode = [...code];
    newCode[index] = value.slice(-1);
    setCode(newCode);
    setError('');

    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }

    if (value && index === 5) {
      const fullCode = newCode.join('');
      if (fullCode.length === 6) {
        handleVerify(fullCode);
      }
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pasted.length > 0) {
      const newCode = [...code];
      for (let i = 0; i < 6; i++) {
        newCode[i] = pasted[i] || '';
      }
      setCode(newCode);
      if (pasted.length === 6) {
        handleVerify(pasted);
      } else {
        inputRefs.current[pasted.length]?.focus();
      }
    }
  };

  const handleVerify = async (fullCode?: string) => {
    const codeStr = fullCode || code.join('');
    if (codeStr.length !== 6 || !selectedMethod || !userId) {
      setError('Please enter all 6 digits');
      return;
    }

    try {
      let response: any;
      if (selectedMethod === 'totp') {
        response = await dispatch(verifyTOTPSetup(userId, totpSecret, codeStr));
        if (response.backupCodes) {
          setBackupCodes(response.backupCodes);
        }
      } else {
        response = await dispatch(verifySMSSetup(userId, codeStr));
      }

      // Show success step
      setActiveStep(selectedMethod === 'totp' ? 3 : 3);

      // Redirect to dashboard after showing backup codes / success
      if (selectedMethod === 'sms' || !response.backupCodes?.length) {
        setTimeout(() => navigate('/dashboard'), 2000);
      }
    } catch (err: any) {
      setError(err.message || 'Invalid code. Please try again.');
      setCode(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
    }
  };

  if (!userId) return null;

  const renderCodeInput = () => (
    <Box
      sx={{ display: 'flex', gap: 1, justifyContent: 'center', mb: 3 }}
      onPaste={handlePaste}
    >
      {code.map((digit, index) => (
        <TextField
          key={index}
          inputRef={(el) => { inputRefs.current[index] = el; }}
          value={digit}
          onChange={(e) => handleCodeChange(index, e.target.value)}
          onKeyDown={(e) => handleKeyDown(index, e as React.KeyboardEvent)}
          inputProps={{
            maxLength: 1,
            style: {
              textAlign: 'center',
              fontSize: '1.5rem',
              fontWeight: 600,
              padding: '12px 0',
            },
            inputMode: 'numeric',
          }}
          sx={{
            width: 48,
            '& .MuiOutlinedInput-root': { borderRadius: 2 },
          }}
          disabled={isLoading}
          autoFocus={index === 0}
        />
      ))}
    </Box>
  );

  // Determine stepper state
  const getActiveStepIndex = () => {
    if (activeStep === 0) return 0;
    if (selectedMethod === 'totp' && activeStep === 1) return 1; // QR code + verify on same step
    if (selectedMethod === 'sms' && activeStep === 1) return 1; // Phone input
    if (selectedMethod === 'sms' && activeStep === 2) return 1; // SMS code verify
    if (activeStep === 3) return 2; // Success
    return activeStep;
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ textAlign: 'center', mb: 3 }}>
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 56,
            height: 56,
            borderRadius: '50%',
            backgroundColor: 'primary.light',
            mb: 2,
          }}
        >
          <SecurityOutlined sx={{ fontSize: 28, color: 'primary.main' }} />
        </Box>
        <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
          Set Up Two-Factor Authentication
        </Typography>
        <Typography variant="body2" color="text.secondary">
          MFA is required to secure your admin account
        </Typography>
      </Box>

      <Stepper activeStep={getActiveStepIndex()} sx={{ mb: 4 }}>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Step 0: Choose Method */}
      {activeStep === 0 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Card variant="outlined" sx={{ borderRadius: 2 }}>
            <CardActionArea onClick={handleSelectTOTP} disabled={setupLoading}>
              <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2.5 }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 48,
                    height: 48,
                    borderRadius: 2,
                    backgroundColor: 'primary.light',
                    flexShrink: 0,
                  }}
                >
                  <Key sx={{ color: 'primary.main' }} />
                </Box>
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    Authenticator App
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Use Google Authenticator, Authy, or any TOTP app
                  </Typography>
                </Box>
              </CardContent>
            </CardActionArea>
          </Card>

          <Card variant="outlined" sx={{ borderRadius: 2 }}>
            <CardActionArea onClick={handleSelectSMS} disabled={setupLoading}>
              <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2.5 }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 48,
                    height: 48,
                    borderRadius: 2,
                    backgroundColor: 'success.light',
                    flexShrink: 0,
                  }}
                >
                  <PhoneAndroid sx={{ color: 'success.main' }} />
                </Box>
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    SMS Verification
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Receive a code via text message to your phone
                  </Typography>
                </Box>
              </CardContent>
            </CardActionArea>
          </Card>

          {setupLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <CircularProgress size={28} />
            </Box>
          )}
        </Box>
      )}

      {/* Step 1 (TOTP): QR Code + Verify */}
      {activeStep === 1 && selectedMethod === 'totp' && (
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Scan this QR code with your authenticator app
          </Typography>

          {qrCodeUrl ? (
            <Box sx={{ mb: 2, display: 'flex', justifyContent: 'center' }}>
              <Box
                component="img"
                src={qrCodeUrl}
                alt="MFA QR Code"
                sx={{
                  width: 200,
                  height: 200,
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  p: 1,
                }}
              />
            </Box>
          ) : (
            <Box sx={{ mb: 2, display: 'flex', justifyContent: 'center' }}>
              <QrCode2 sx={{ fontSize: 120, color: 'text.disabled' }} />
            </Box>
          )}

          {manualEntryCode && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                Or enter this key manually:
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  backgroundColor: 'action.hover',
                  px: 2,
                  py: 1,
                  borderRadius: 1,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontFamily: 'monospace',
                    fontWeight: 600,
                    letterSpacing: '0.05em',
                    wordBreak: 'break-all',
                    flex: 1,
                  }}
                >
                  {manualEntryCode}
                </Typography>
                <Tooltip title={copied ? 'Copied!' : 'Copy key'}>
                  <IconButton
                    size="small"
                    onClick={() => {
                      navigator.clipboard.writeText(manualEntryCode);
                      setCopied(true);
                      setTimeout(() => setCopied(false), 2000);
                    }}
                  >
                    {copied ? <Check fontSize="small" color="success" /> : <ContentCopy fontSize="small" />}
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
          )}

          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Enter the 6-digit code from your app to confirm setup
          </Typography>

          {renderCodeInput()}

          <Button
            fullWidth
            variant="contained"
            size="large"
            onClick={() => handleVerify()}
            disabled={isLoading || code.join('').length !== 6}
          >
            {isLoading ? <CircularProgress size={24} color="inherit" /> : 'Verify & Activate'}
          </Button>
        </Box>
      )}

      {/* Step 1 (SMS): Phone on file — send code, or show contact admin message */}
      {activeStep === 1 && selectedMethod === 'sms' && (
        <Box sx={{ textAlign: 'center' }}>
          {hasPhoneOnFile ? (
            <>
              <PhoneAndroid sx={{ fontSize: 48, color: 'success.main', mb: 2 }} />
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                A verification code will be sent to your phone
                {phoneHint ? ` ending in ${phoneHint}` : ''}.
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 3 }}>
                This number was registered by your super admin.
              </Typography>

              <Button
                fullWidth
                variant="contained"
                size="large"
                onClick={handleSendSmsCode}
                disabled={setupLoading}
              >
                {setupLoading ? <CircularProgress size={24} color="inherit" /> : 'Send Verification Code'}
              </Button>
            </>
          ) : (
            <>
              <ErrorOutline sx={{ fontSize: 48, color: 'warning.main', mb: 2 }} />
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                Phone Number Not Found
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Your phone number has not been added by a super admin.
                Please contact your super admin to register your phone number,
                or choose a different authentication method.
              </Typography>

              <Button
                fullWidth
                variant="outlined"
                size="large"
                onClick={() => {
                  setActiveStep(0);
                  setSelectedMethod(null);
                  setHasPhoneOnFile(null);
                  setError('');
                }}
                sx={{ mb: 2 }}
              >
                Choose a Different Method
              </Button>

              <Typography variant="caption" color="text.secondary">
                Ask your super admin to add your phone number in the admin management panel.
              </Typography>
            </>
          )}
        </Box>
      )}

      {/* Step 2 (SMS): Verify Code */}
      {activeStep === 2 && selectedMethod === 'sms' && (
        <Box sx={{ textAlign: 'center' }}>
          <PhoneAndroid sx={{ fontSize: 48, color: 'success.main', mb: 2 }} />
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            We sent a verification code to your phone
            {phoneHint ? ` ending in ${phoneHint}` : ''}.
            Enter the 6-digit code below.
          </Typography>

          {renderCodeInput()}

          <Button
            fullWidth
            variant="contained"
            size="large"
            onClick={() => handleVerify()}
            disabled={isLoading || code.join('').length !== 6}
          >
            {isLoading ? <CircularProgress size={24} color="inherit" /> : 'Verify & Activate'}
          </Button>
        </Box>
      )}

      {/* Step 3: Success */}
      {activeStep === 3 && (
        <Box sx={{ textAlign: 'center' }}>
          <CheckCircleOutline sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
            MFA Activated Successfully
          </Typography>

          {backupCodes.length > 0 && (
            <Box sx={{ mb: 3, textAlign: 'left' }}>
              <Alert severity="warning" sx={{ mb: 2 }}>
                Save these backup codes in a safe place. You can use them to sign in if you lose access to your authenticator app.
              </Alert>
              <Box
                sx={{
                  backgroundColor: 'action.hover',
                  borderRadius: 2,
                  p: 2,
                  fontFamily: 'monospace',
                  fontSize: '0.9rem',
                  lineHeight: 2,
                  userSelect: 'all',
                }}
              >
                {backupCodes.map((c, i) => (
                  <Box key={i}>{c}</Box>
                ))}
              </Box>
            </Box>
          )}

          <Button
            fullWidth
            variant="contained"
            size="large"
            onClick={() => navigate('/dashboard')}
          >
            {backupCodes.length > 0 ? 'I\'ve saved my backup codes — Continue' : 'Continue to Dashboard'}
          </Button>
        </Box>
      )}

      {/* Back link */}
      {activeStep < 3 && (
        <Box sx={{ textAlign: 'center', mt: 3 }}>
          <Link
            onClick={() => {
              if (activeStep > 0) {
                setActiveStep(0);
                setSelectedMethod(null);
                setHasPhoneOnFile(null);
                setCode(['', '', '', '', '', '']);
                setError('');
              } else {
                navigate('/auth/login');
              }
            }}
            variant="body2"
            sx={{ cursor: 'pointer' }}
          >
            {activeStep > 0 ? 'Choose a different method' : 'Back to login'}
          </Link>
        </Box>
      )}
    </Box>
  );
};
